/** @typedef {import('../index.js').BaseDocData} BaseDocData */
import * as Yup from 'yup';

import YupFirestoreTimestamp from '../yupFirestoreTimestamp.js';

export const easypostWebhookEventSchema = Yup.object().shape({
  description: Yup.string().required('Event description is required'),
  trackingCode: Yup.string().optional(),
  status: Yup.string().optional(),
  // set by the server on write; only present when reading a stored claim back
  receivedAt: YupFirestoreTimestamp.optional(),
});

/**
 * @typedef {Object} EasypostWebhookEventSchema
 * @property {string} description
 * @property {string} [trackingCode]
 * @property {string} [status]
 * @property {any} [receivedAt]
 *
 * @typedef {BaseDocData & EasypostWebhookEventSchema} EasypostWebhookEventDocData
 */

/**
 * Dedupe record for a received EasyPost webhook event, stored under the EasyPost
 * event id. EasyPost retries deliveries it considers failed (e.g. a cold start
 * exceeding its client timeout even though processing finished), so the webhook
 * handler claims the event id with a `create()` before processing and skips
 * events whose record already exists.
 */
export default class EasypostWebhookEvent {
  /** @type {EasypostWebhookEventDocData['validation']} */ validation = 'passed';
  /** @type {EasypostWebhookEventDocData['id']} */ id;
  /** @type {EasypostWebhookEventDocData['description']} */ description;
  /** @type {EasypostWebhookEventDocData['trackingCode']} */ trackingCode;
  /** @type {EasypostWebhookEventDocData['status']} */ status;
  /** @type {EasypostWebhookEventDocData['receivedAt']} */ receivedAt;

  /**
   * @param {EasypostWebhookEventDocData} param0
   */
  constructor({
    id,
    description,
    trackingCode,
    status,
    receivedAt,
  }) {
    const validatedData = easypostWebhookEventSchema.validateSync({
      description,
      trackingCode,
      status,
      receivedAt,
    });

    this.id = id || null;
    this.description = validatedData.description;
    this.trackingCode = validatedData.trackingCode;
    this.status = validatedData.status;
    this.receivedAt = validatedData.receivedAt;
  }

  toFirestore({ serverTimestamp }) {
    return Object.fromEntries(Object.entries({
      description: this.description,
      trackingCode: this.trackingCode,
      status: this.status,
      receivedAt: this.receivedAt || serverTimestamp(),
    }).filter(([, value]) => value !== undefined));
  }

  static fromFirestore(docData) {
    return new EasypostWebhookEvent(docData);
  }
}
